import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ToggleLeft, ToggleRight } from 'lucide-react';
import { authservice } from '../../../../components/api/authservice';
import { useAccessTheme } from './useAccessTheme';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  userId?: string;
}

const tenantId = '6a6eee6f-75d7-4479-892d-80b5f225e004';

const AddUserModal: React.FC<Props> = ({ onClose, onSubmit, userId }) => {
  const t = useAccessTheme();
  const isEditMode = !!userId;
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    roleID: '',
    isActive: true,
    tenantId: '',
  });
  const [error, setError] = useState('');
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (isEditMode) {
      authservice.getSingleUser(tenantId, userId!).then(res => {
        const { user } = res;
        setForm({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          password: '',
          roleID: user.roleID || '',
          isActive: user.isActive,
          tenantId: user.tenantId || '',
        });
      }).catch(console.error);
    }
  }, [userId, isEditMode]);

  useEffect(() => {
    authservice.getRoles()
      .then(res => {
        setRoles((res || []).map((r: any) => ({
          id: r.roleId,
          name: r.roleName || 'Unnamed Role',
        })));
      })
      .catch(err => console.error('Failed to fetch roles', err));
  }, []);

  const handleChange = (field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.roleID) {
      setError('All fields marked with * are required.');
      return;
    }
    if (!isEditMode && form.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    try {
      if (isEditMode) {
        await authservice.editUser({
          id: userId,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          tenantId: form.tenantId,
          roleID: form.roleID,
          isactive: form.isActive,
        });
      } else {
        await authservice.registerUser({
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          tenant_id: form.tenantId,
          isactive: form.isActive,
          createdBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
          roleID: form.roleID,
        });
      }
      onSubmit();
    } catch (err) {
      setError('Failed to process request. Please try again.');
      console.error(err);
    }
  };

  const field = (label: string, children: React.ReactNode) => (
    <div>
      <label className={`block text-xs font-medium mb-1.5 ${t.muted}`}>{label}</label>
      {children}
    </div>
  );

  return createPortal(
    <div className={`fixed inset-0 z-[150] flex items-center justify-center p-4 ${t.overlay}`}>
      <div className={`rounded-xl shadow-xl w-full max-w-md overflow-hidden border ${t.panel}`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${t.header}`}>
          <h3 className={`text-base font-semibold ${t.title}`}>
            {isEditMode ? 'Edit User' : 'Add New User'}
          </h3>
          <button type="button" onClick={onClose} className={`p-1.5 rounded-lg ${t.closeBtn}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {field('First Name *', (
              <input type="text" value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm outline-none ${t.input}`} placeholder="John" />
            ))}
            {field('Last Name *', (
              <input type="text" value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm outline-none ${t.input}`} placeholder="Doe" />
            ))}
          </div>
          {field('Email *', (
            <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm outline-none ${t.input}`} placeholder="john.doe@company.com" />
          ))}
          {!isEditMode && field('Password *', (
            <input type="password" value={form.password} onChange={(e) => handleChange('password', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm outline-none ${t.input}`} placeholder="Min. 8 characters" />
          ))}
          {field('Role *', (
            <select value={form.roleID} onChange={(e) => handleChange('roleID', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm outline-none ${t.input}`}>
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          ))}

          <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${t.border}`}>
            <div>
              <div className={`text-sm font-medium ${t.title}`}>Active</div>
              <div className={`text-xs ${t.muted}`}>Allow user to sign in</div>
            </div>
            <button type="button" onClick={() => handleChange('isActive', !form.isActive)}>
              {form.isActive
                ? <ToggleRight className={`w-8 h-8 ${t.toggleOn}`} />
                : <ToggleLeft className={`w-8 h-8 ${t.toggleOff}`} />}
            </button>
          </div>

          {error && <div className={`border rounded-lg px-3 py-2 text-xs ${t.errorBox}`}>{error}</div>}
        </div>

        <div className={`px-5 py-4 border-t flex justify-end gap-2 ${t.footer}`}>
          <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${t.btnGhost}`}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className={`px-4 py-2 rounded-lg text-sm font-medium ${t.btnPrimary}`}>
            {isEditMode ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddUserModal;
